import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ControlledInput, Modal, Typography } from 'components';
import { SMS_MFA } from 'constants/challengeNames';
import { toaster } from 'helpers/toaster';
import useAuth from 'hooks/useAuth';
import { CloseIcon, ErrorModalFeaturedIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useClientAccountDestroy } from 'providers/UserProvider/useClientAccountDestroy';
import { useCurrentUser } from 'providers/UserProvider/useCurrentUser';
import { FC, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { TScheme } from 'types/TScheme';
import { cn } from 'utils/cn';
import * as yup from 'yup';
import YupPassword from 'yup-password';

YupPassword(yup);

interface IModalDeleteAccount {
  open: boolean;
  onCancel: () => void;
}

interface VerifyPhoneNumberInput {
  email: string;
  password: string;
}

const ModalDeleteAccount: FC<IModalDeleteAccount> = ({ open, onCancel }) => {
  const t = useTranslations();
  const router = useRouter();
  const auth = useAuth();
  const { data: currentUser } = useCurrentUser();

  const refModal = useRef<HTMLDivElement>(null);

  const { mutateAsync: deleteAccount } = useClientAccountDestroy();

  const schema = yup.object().shape({
    email: yup
      .string()
      .nullable()
      .email(t('messages.fields.email_validation'))
      .required(t('messages.fields.email_required')),
    password: yup.string().password().required(t('messages.fields.password_required'))
  } as TScheme<VerifyPhoneNumberInput>);

  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    getValues
  } = useForm<VerifyPhoneNumberInput>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      email: currentUser?.email
    }
  });

  const disabled = !(isDirty && isValid);

  const handleDelete = useCallback(async () => {
    try {
      const { email, password } = getValues();
      const user = await auth.signIn(email, password);
      if (user?.attributes?.email_verified || user?.challengeName === SMS_MFA) {
        await deleteAccount({});
        return router.push('/auth/sign-out');
      }

      throw new Error();
    } catch (error) {
      notifyException(error, ESpansException.AccountDetailsException);
      toaster.error(t('messages.error.deleting'));
    }
  }, [auth, deleteAccount, getValues, router, t]);

  const onSubmit = handleSubmit(handleDelete);

  return (
    <Modal
      zDialog={cn('!z-[1999]')}
      zDialogBackdrop="z-[1000]"
      onClose={onCancel}
      open={open}
      className={'max-h-[60vh] w-[544px]'}
    >
      <div className="absolute right-2 top-2 z-[1000]">
        <Button
          size="small"
          variant="icon"
          color="neutral"
          className="!w-8 rounded-full"
          onClick={onCancel}
          startAdornment={<CloseIcon />}
        />
      </div>
      <div
        className={cn(
          'overflow-y-auto px-8 py-6',
          'relative flex w-full flex-col justify-center  gap-2 rounded bg-white shadow-custom'
        )}
        ref={refModal}
      >
        <ErrorModalFeaturedIcon />
        <Typography variant="medium4xl" className=" text-tertiary-100 ">
          {t('account_details.delete.title')}
        </Typography>
        <Typography variant="light4" className="mt-2 flex">
          <div className="max-w-xl text-justify">{t('account_details.delete.paragraph')}</div>
        </Typography>
        <div className="mt-6">
          <ControlledInput
            control={control}
            error={errors.email}
            label={t('auth.sign_in.label_email')}
            type="text"
            name="email"
            classNameParent="w-full"
            disabled
          />
          <ControlledInput
            control={control}
            error={errors.password}
            label={t('auth.sign_in.label_password')}
            type="password"
            name="password"
            classNameParent="w-full"
          />
        </div>
        <div className="mt-4 flex justify-end gap-4">
          <Button
            label={t('account_details.cancel')}
            onClick={onCancel}
            size="medium"
            className="flex items-center"
            variant="text"
          />
          <Button
            label={t('account_details.delete.button')}
            onClick={onSubmit}
            color="danger"
            size="medium"
            className="flex items-center"
            variant="contained"
            disabled={disabled}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ModalDeleteAccount;
